/**
 * HC: opening hours for the store page's LocalBusiness JSON-LD.
 *
 * Derived at runtime from the rendered hours rows (their data-hclo-*
 * contract - the same markup the visible list and the open/closed badge
 * use), so there is ONE source of truth and editor changes flow through
 * automatically. Special hours rows (data-hclo-date) become
 * specialOpeningHoursSpecification; Google reads a closed day as 00:00-00:00.
 *
 * Why not server-side Liquid: nested theme blocks expose ids but no
 * settings through block.blocks (verified on a development theme), and
 * {% content_for 'blocks' %} output cannot be captured into a string -
 * capturing it renders nothing at all. Google executes page JS and reads
 * JSON-LD injected this way; without JS the schema still carries the
 * complete name/address/phone/geo payload.
 */
(() => {
  const TOKENS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  /** "10AM" / "7:30 pm" / "10" -> "10:00" | "19:30", or null if unparseable */
  const to24 = (raw) => {
    if (!raw) return null;
    const m = raw.trim().toLowerCase().replace(/[\s.]/g, '').match(/^(\d{1,2})(?::(\d{2}))?(am|pm)?$/);
    if (!m) return null;
    let hour = parseInt(m[1], 10);
    const minutes = m[2] || '00';
    const meridiem = m[3];
    if (!meridiem) {
      if (hour > 23) return null;
    } else {
      if (hour < 1 || hour > 12) return null;
      if (meridiem === 'pm' && hour !== 12) hour += 12;
      if (meridiem === 'am' && hour === 12) hour = 0;
    }
    return String(hour).padStart(2, '0') + ':' + minutes;
  };

  const buildSpecs = () => {
    const specs = [];
    for (const row of document.querySelectorAll('[data-hclo-days]')) {
      if (row.dataset.hcloClosed === 'true') continue;
      const label = (row.dataset.hcloDays || '').toLowerCase();
      let first = -1;
      let last = -1;
      TOKENS.forEach((token, i) => {
        if (label.includes(token)) {
          if (first === -1) first = i;
          last = i;
        }
      });
      if (first === -1) continue;
      const opens = to24(row.dataset.hcloOpens);
      const closes = to24(row.dataset.hcloCloses);
      if (!opens || !closes) continue;
      specs.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: NAMES.slice(first, last + 1),
        opens,
        closes,
      });
    }
    return specs;
  };

  const buildSpecialSpecs = () => {
    const specs = [];
    for (const row of document.querySelectorAll('[data-hclo-date]')) {
      const closed = row.dataset.hcloClosed === 'true';
      const opens = closed ? '00:00' : to24(row.dataset.hcloOpens);
      const closes = closed ? '00:00' : to24(row.dataset.hcloCloses);
      if (!opens || !closes) continue;
      specs.push({
        '@type': 'OpeningHoursSpecification',
        validFrom: row.dataset.hcloDate,
        validThrough: row.dataset.hcloDateEnd || row.dataset.hcloDate,
        opens,
        closes,
      });
    }
    return specs;
  };

  const apply = () => {
    const script = document.querySelector('script[type="application/ld+json"][data-hc-store-schema]');
    if (!script) return;
    const specs = buildSpecs();
    const specialSpecs = buildSpecialSpecs();
    if (!specs.length && !specialSpecs.length) return;
    try {
      const data = JSON.parse(script.textContent);
      if (specs.length) data.openingHoursSpecification = specs;
      if (specialSpecs.length) data.specialOpeningHoursSpecification = specialSpecs;
      script.textContent = JSON.stringify(data, null, 2);
    } catch (error) {
      /* leave the server-rendered schema untouched */
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true });
  } else {
    apply();
  }
})();
